/* global window, document, console, setTimeout */
import React, { useState } from "react";
import { createRoot } from "react-dom/client";
import Modal from "./Modal";
import Heroicon from "./Heroicon";

function EpisodeLinks({
  id,
  userId,
  embed,
  file,
  youtube,
  isHeadliner,
  headliner,
}) {
  let [isOpen, setIsOpen] = useState(false);
  let [loading, setLoading] = useState(false);
  let [isThereUrl, setIsThereUrl] = useState(null);
  let [isThereVideo, setIsThereVideo] = useState(null);
  let [isThereMessage, setIsThereMessage] = useState(null);

  function closeModal() {
    setIsOpen(false);
    setIsThereMessage(null);
  }

  function openModal() {
    setIsOpen(true);
  }

  function getParamsForVideoUrl(userId, episodeId) {
    setLoading(true);

    window.axios
      .get("/headliner/get-headliner-params", {
        params: {
          episodeId: episodeId,
        },
      })
      .then(function (response) {
        const customTraceId = response.data.response.customTraceId;
        const headlinerId = response.data.response.headlinerId;
        const traceId = response.data.response.traceId;

        getHeadlinerVideoUrl(
          userId,
          customTraceId,
          headlinerId,
          episodeId,
          traceId
        );
      })
      .catch(function (error) {
        console.log(error);
      });
  }

  function getHeadlinerVideoUrl(
    e,
    userId,
    customTraceId,
    headlinerId,
    episodeId
  ) {
    e.preventDefault();

    if (!loading) {
      setLoading(true);
    }

    window.axios
      .get("/headliner/get-video-url", {
        params: {
          userId: userId,
          customTraceId: customTraceId,
          headlinerId: headlinerId,
        },
      })
      .then(function (response) {
        setLoading(false);

        // If response is successful, set state to the response URL
        if (response.data.status === "success") {
          // response.data.response is the video URL
          setIsThereUrl(response.data.response);
        }

        // The response returns an error status if the Headliner video hasn't finished processing yet
        // Or if the user didn't complete the video setup
        if (response.data.status === "error") {
          if (
            response.data.response ===
            "The video is not ready yet! Please try again a bit later."
          ) {
            setIsThereMessage({
              status: "info",
              title: "Video is being processed",
              message: response.data.response,
            });
          } else {
            // If user created a Headliner video instance but didn't complete the set up so there's no URL created
            // Launch Headliner wizard in new tab so user can finish creating the video
            triggerHeadliner(userId, episodeId, "active");

            setIsThereMessage({
              status: "info",
              title:
                "Please try creating a video with the Headliner wizard link again",
              message: "Launching Headliner wizard",
            });
          }
        }
      })
      .catch(function (error) {
        console.log(error);
      });
  }

  function triggerHeadliner(userId, episodeId, status) {
    if (!loading) {
      setLoading(true);
    }

    window.axios
      .get("/oauth2/trigger-headliner", {
        params: {
          episodeId: episodeId,
        },
      })
      .then(function (response) {
        // Get the response URL
        // This link opens the Headliner wizard to start the video setup
        const url = response.data.url;

        if (url === "empty") {
          setLoading(false);
          setIsThereMessage({
            status: "danger",
            title: response.data.response,
            message: "Something went wrong, please try again.",
          });
        } else {
          // Show success message
          if (status !== "active") {
            setIsThereMessage({
              status: "success",
              title: "Create a video with the Headliner wizard",
              message: "Launching Headliner wizard",
            });
          }

          // Open link in new tab
          setTimeout(function () {
            window.open(url, "_blank");
            setLoading(false);
          }, 1700);

          // Set video state to true
          setIsThereVideo(true);
        }
      })
      .catch(function (error) {
        console.error(error);
        setIsThereMessage({
          status: "danger",
          title: "Error creating video",
          message: error.response.data.response,
        });
      });
  }

  return (
    <>
      <button
        aria-label="Episode links"
        onClick={openModal}
        className="flex items-center flex-1 w-full h-full gap-2 p-3 text-sm text-gray-500 md:justify-center md:p-4 focus:rounded-md hover:bg-gray-100 focus:outline-none focus:ring-4 focus:ring-gray-300"
      >
        <Heroicon icon="o-link" style="w-4 h-4 shrink-0" />
        Media Links
      </button>

      {!isThereMessage ? (
        <Modal title="Episode links" isOpen={isOpen} closeModal={closeModal}>
          <div className="px-6 py-4">
            <p>Embed code:</p>
            <code>
              <textarea
                rows="4"
                className="w-full m-0 my-4 text-sm font-medium text-blue-600 border border-blue-200 rounded-md resize-none bg-blue-50"
                value={`<iframe class="castos-iframe-player" src="${
                  embed + id
                }" width="100%" height="150"></iframe>`}
                readOnly
              ></textarea>
            </code>
            <hr />
            <p className="mt-4">Episode file:</p>

            {file && (
              <div className="flex items-stretch mt-2">
                <label htmlFor={`episode${id}`} className="sr-only">
                  Episode file
                </label>
                <input
                  id={`episode${id}`}
                  className="bg-gray-100 rounded-r-none form-control episode-links input"
                  type="text"
                  value={file}
                  readOnly
                />
                <button
                  className="w-auto bg-gray-300 rounded-l-none cursor-pointer input-group-addon input"
                  onClick={() => Castos.helpers.copyToClipboard(`episode${id}`)}
                  aria-label="Copy file"
                >
                  <Heroicon
                    icon="o-clipboard-document"
                    style="w-4 h-4 text-gray-600"
                  />
                </button>
              </div>
            )}

            {youtube && (
              <>
                <p className="mt-6">YouTube link:</p>
                <div className="flex items-stretch mt-2">
                  <label htmlFor={`yt-id-${id}`} className="sr-only">
                    YouTube link
                  </label>
                  <input
                    id={`yt-id-${id}`}
                    className="bg-gray-100 rounded-r-none form-control episode-links input"
                    type="text"
                    value={"https://www.youtube.com/watch?v=" + youtube}
                    readOnly
                  />
                  <button
                    className="w-auto bg-gray-300 rounded-l-none cursor-pointer input-group-addon input"
                    onClick={() =>
                      Castos.helpers.copyToClipboard(`yt-id-${id}`)
                    }
                  >
                    <Heroicon
                      icon="o-clipboard-document"
                      style="w-4 h-4 text-gray-600"
                    />
                  </button>
                </div>
              </>
            )}

            {/* Show the following section if the user's plan covers Headliner integration */}

            {isHeadliner && file && (
              <div className="mt-6">
                {/* If Headliner is active and there's a video URL created */}
                {/* Show Headliner video URL button that takes you to the page where you can download it */}

                {headliner && headliner.video_url && (
                  <a
                    href={headliner.video_url}
                    rel="noopener noreferrer"
                    target="_blank"
                    className="btn-solid btn-secondary"
                  >
                    Headliner video URL
                    <Heroicon
                      icon="o-video-camera"
                      style="w-4 h-4 inline ml-2"
                    />
                  </a>
                )}

                {/* If user created a Headliner video instance but there's no URL yet */}
                {/* Show Get Headliner URL button */}

                {headliner && !headliner.video_url && (
                  <a
                    href={isThereUrl === null ? "#" : isThereUrl}
                    onClick={(e) =>
                      isThereUrl === null
                        ? getHeadlinerVideoUrl(
                            e,
                            userId,
                            headliner.custom_trace_id,
                            headliner.id,
                            id,
                            headliner.trace_id
                          )
                        : ""
                    }
                    className="cursor-pointer btn-solid btn-secondary"
                    target={isThereUrl && "_blank"}
                  >
                    <Heroicon
                      icon="o-video-camera"
                      style="w-4 h-4 inline mr-2"
                    />
                    {/* Once URL is available, show Headliner video URL button */}
                    {isThereUrl === null
                      ? "Get Headliner video URL"
                      : "Headliner video URL"}

                    {loading && (
                      <Heroicon
                        icon="o-arrow-path"
                        style="w-4 h-4 inline ml-1 animate-spin"
                      />
                    )}
                  </a>
                )}

                {/* If there's no Headliner video instance */}
                {/* Show Create Video button */}

                {!headliner && (
                  <>
                    <button
                      onClick={() => triggerHeadliner(userId, id, "inactive")}
                      className={`btn-solid btn-secondary cursor-pointer ${
                        isThereVideo && "hidden"
                      }`}
                    >
                      Create video using Headliner
                      {loading && (
                        <Heroicon
                          icon="o-arrow-path"
                          style="w-4 h-4 inline ml-1 animate-spin"
                        />
                      )}
                    </button>

                    {/* Once the video instance is available */}
                    {/* Show Get Headliner URL button */}
                    {isThereVideo && (
                      <a
                        href={isThereUrl === null ? "#" : isThereUrl}
                        onClick={() =>
                          isThereUrl === null
                            ? getParamsForVideoUrl(userId, id)
                            : ""
                        }
                        className="btn-solid btn-secondary"
                        target={isThereUrl && "_blank"}
                      >
                        <Heroicon
                          icon="o-video-camera"
                          style="w-4 h-4 inline mr-2"
                        />
                        {/* Once URL is available, show Headliner video URL button */}
                        {isThereUrl === null
                          ? "Get Headliner video URL"
                          : "Headliner video URL"}

                        {loading && (
                          <Heroicon
                            icon="o-arrow-path"
                            style="w-4 h-4 inline ml-1 animate-spin"
                          />
                        )}
                      </a>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 px-6 py-4 mt-6 bg-gray-200">
            <button className="btn-solid btn-dark" onClick={closeModal}>
              Close
            </button>
          </div>
        </Modal>
      ) : (
        <Modal title="Episode links" isOpen={isOpen} closeModal={closeModal}>
          <div
            className={`p-4 m-4 text-sm sm:text-base rounded-md flex-col alert alert--${isThereMessage.status}`}
          >
            <img
              src={`/assets/icons/icon-${isThereMessage.status}.svg`}
              alt=""
              aria-hidden="true"
              className="block w-10 h-10 sm:w-16 sm:h-16"
            />
            <h2 className="mt-5 text-xl text-center sm:text-2xl">
              {isThereMessage.title}
            </h2>
            {isThereMessage.status === "info" ||
            isThereMessage.status === "success" ? (
              <p className="mt-2 text-center">
                {isThereMessage.message}{" "}
                {loading ? (
                  <Heroicon
                    icon="o-arrow-path"
                    style="w-4 h-4 inline ml-1 animate-spin"
                  />
                ) : (
                  <Heroicon icon="o-check-circle" style="w-4 h-4 inline ml-1" />
                )}
              </p>
            ) : (
              <p className="mt-2">{isThereMessage.message}</p>
            )}
          </div>
          <div className="flex justify-end px-6 py-4 mt-6 bg-gray-200">
            <button className="btn-solid btn-dark" onClick={closeModal}>
              Close
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

const initEpisodeLinks = function (selector) {
  const elements = document.querySelectorAll(selector);
  if (elements.length > 0) {
    elements.forEach((element) => {
      const root = createRoot(element);
      const id = element.dataset.id;
      const userId = element.dataset.userid;
      const embed = element.dataset.embed;
      const file = element.dataset.file;
      const youtube = element.dataset.youtube;
      const isHeadliner = element.dataset.isheadliner;
      const headliner = JSON.parse(element.dataset.headliner);
      root.render(
        <EpisodeLinks
          id={id}
          embed={embed}
          file={file}
          youtube={youtube}
          isHeadliner={isHeadliner}
          headliner={headliner}
          userId={userId}
        />
      );
    });
  }
};
export { EpisodeLinks as default, initEpisodeLinks };
